import type { ReactNode } from 'react';
import { ArrowRight, Lock } from '../../../components/ui/icons';
import { StakeLadderHead } from '../../../components/play/cycling-ui';

/** Shared section chrome — mockup padding and card shell for every hub compete state. */
export function CyclingDuelsHubSection({
  pager,
  eyebrow = 'Duels of the day',
  title,
  body,
  lockAt,
  now,
  badge,
  children,
  cta,
  ctaNote,
  secondaryAction
}: {
  pager?: ReactNode;
  eyebrow?: string;
  title: string;
  body: ReactNode;
  lockAt?: number | null;
  now?: number;
  /** Override lock countdown — e.g. Live pill when racing. */
  badge?: ReactNode;
  children?: ReactNode;
  cta?: {
    label: string;
    onClick: () => void;
    variant: 'primary' | 'dark' | 'disabled';
    icon?: 'lock' | 'arrow';
  };
  ctaNote?: string;
  /** Low-emphasis text link under the card — e.g. "Edit my calls" while ranking. */
  secondaryAction?: { label: string; onClick: () => void };
}) {
  return (
    <div>
      {pager}

      <div className="mt-3 overflow-hidden rounded-[26px] border border-black/[0.08] bg-white shadow-[0_24px_50px_-28px_rgba(20,20,50,0.36)]">
        <StakeLadderHead
          eyebrow={eyebrow}
          title={title}
          body={body}
          lockAt={lockAt}
          now={now}
          badge={badge}
        />
        {children ?? <div className="h-5 pb-5" />}
      </div>

      {cta ? (
        <div className="pt-4">
          <HubCta {...cta} />
          {ctaNote ? (
            <p className="mt-2 text-center text-[11px] font-semibold text-ink3">{ctaNote}</p>
          ) : null}
        </div>
      ) : ctaNote ? (
        <p className="pt-3 text-center text-[11px] font-semibold text-ink3">{ctaNote}</p>
      ) : null}

      {secondaryAction ? (
        <div className="pt-2.5 text-center">
          <button
            type="button"
            onClick={secondaryAction.onClick}
            className="tap text-[12.5px] font-bold tracking-[-0.01em] text-accent-ink underline decoration-accent-ink/30 underline-offset-[3px]"
          >
            {secondaryAction.label}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function HubCta({
  label,
  onClick,
  variant,
  icon = 'arrow'
}: {
  label: string;
  onClick: () => void;
  variant: 'primary' | 'dark' | 'disabled';
  icon?: 'lock' | 'arrow';
}) {
  if (variant === 'disabled') {
    return (
      <div className="flex items-center justify-center gap-2 rounded-2xl bg-[#EDEDF1] p-[15px]">
        <span className="text-[15px] font-extrabold tracking-[-0.01em] text-[#B0B0B8]">{label}</span>
        <ArrowRight width={15} height={15} strokeWidth={2.8} className="text-[#B0B0B8]" />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={
        variant === 'dark'
          ? 'tap flex w-full items-center justify-center gap-2 rounded-[17px] bg-night p-4 shadow-dark'
          : 'tap flex w-full items-center justify-center gap-2 rounded-[17px] bg-[linear-gradient(140deg,#2EDB7B,#0BA078)] p-4 shadow-[0_18px_34px_-16px_rgba(11,160,120,0.7)]'
      }
    >
      {icon === 'lock' ? <Lock width={16} height={16} strokeWidth={2.4} className="text-white" /> : null}
      <span className="text-[15px] font-extrabold tracking-[-0.01em] text-white">{label}</span>
      <ArrowRight width={15} height={15} strokeWidth={2.8} className="text-white" />
    </button>
  );
}

export function HubLiveBadge() {
  return (
    <div className="inline-flex shrink-0 items-center gap-[5px] rounded-full border border-white/[0.08] bg-[linear-gradient(150deg,#22232C,#0E0E14)] px-[9px] py-[5px] shadow-[0_6px_14px_-8px_rgba(0,0,0,0.5)]">
      <span className="h-[6px] w-[6px] animate-h2h-pulse rounded-full bg-[#EF4135]" />
      <span className="num text-[12.5px] font-extrabold tracking-[-0.01em] text-[#FF6B61]">Live</span>
    </div>
  );
}

export function HubProgressBar({ done, total }: { done: number; total: number }) {
  return (
    <div className="flex items-center gap-2.5 px-[18px] pb-5">
      <div className="h-1.5 flex-1 overflow-hidden rounded-[3px] bg-[#EBEBF0]">
        <div
          className="h-full rounded-[3px] bg-[linear-gradient(90deg,#2EDB7B,#0BA078)] transition-[width]"
          style={{ width: `${Math.max(4, (done / total) * 100)}%` }}
        />
      </div>
      <span className="num shrink-0 text-xs font-extrabold text-accent-ink">
        {done} / {total} called
      </span>
    </div>
  );
}
